import { ProjectContext } from '../brain/ProjectContext';
import { WorkSyncPanel } from "../tasks/WorkSyncPanel";
import { useEffect, useState, type FormEvent } from "react";
import { Activity, Building2, CalendarDays, CircleDashed, Flag, Shapes, Target, Trash2, UserRound, UsersRound, X } from "lucide-react";
import { TaskCheck, type TaskStatus } from "../../ui/Priority";
import { PeoplePicker } from "../../ui/PeoplePicker";
import { Link, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { getWorkMetrics, updateTask } from "../../api/hub/work";
import {
  EvidenceLinks,
  ownerLabel,
  TaskFlags,
  sourceLabel,
} from "../tasks/TaskMeta";
import { taskDate } from "../tasks/time";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { hubRequest } from "../../api/hub/client";
import {
  BUSINESS_DOMAINS,
  type Project,
  type OkrObjective,
  type ProjectUpdate,
} from "../../api/hub/types";
import { useWorkDirectory, statusLabels } from "../tasks/useTasks";
import { projectStatus, projectHealth, useProjects } from "./useProjects";
import "./projects.css";
export function ProjectsPage() {
  const [view, setView] = useState("table"),
    [expandedProjects, setExpandedProjects] = useState<string[]>([]);
  const metrics = useQuery({
    queryKey: ["hub", "work-metrics"],
    queryFn: getWorkMetrics,
  });
  const qc = useQueryClient();
  const taskEdit = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: Parameters<typeof updateTask>[1];
    }) => updateTask(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["hub"] }),
  });
  const [params, setParams] = useSearchParams();
  const work = useProjects(),
    directory = useWorkDirectory(),
    [group, setGroup] = useState("domain"),
    [editing, setEditing] = useState<Project | null>(null),
    [creating, setCreating] = useState(false),
    [adopt, setAdopt] = useState(false),
    [guid, setGuid] = useState(""),
    [adoptTitle, setAdoptTitle] = useState(""),
    [error, setError] = useState("");
  const customers = useQuery({
      queryKey: ["hub", "customers"],
      queryFn: () =>
        hubRequest<{ id: string; name?: string; title?: string }[]>(
          "/customers",
        ),
    }),
    okr = useQuery({
      queryKey: ["hub", "okr"],
      queryFn: () => hubRequest<OkrObjective[]>("/okr"),
    });
  const projects = work.projects.data ?? [],
    groups =
      group === "health"
        ? Object.keys(projectHealth)
        : [
            ...BUSINESS_DOMAINS,
            ...new Set(
              projects
                .map((p) => p.domain)
                .filter(
                  (d) =>
                    !BUSINESS_DOMAINS.includes(
                      d as (typeof BUSINESS_DOMAINS)[number],
                    ),
                ),
            ),
          ];
  const active =
    projects.find((p) => p.id === params.get("project")) ||
    (editing && projects.find((p) => p.id === editing.id)) ||
    editing;
  return (
    <section className="work-page">
      <WorkSyncPanel />
      <header className="work-heading">
          <h1>进度表</h1>
          <p className="work-stats">
            <span><b>{projects.length}</b> 个项目</span>
            {(() => {
              const risky = projects.filter((p) => p.health !== "on_track").length;
              return risky > 0 ? <span data-tone="bad"><b>{risky}</b> 有风险</span> : null;
            })()}
          </p>
        <div className="work-heading-actions">
          <button onClick={() => setAdopt(!adopt)}>纳入飞书清单</button>
          <button className="work-primary" onClick={() => setCreating(true)}>
            新建项目
          </button>
        </div>
      </header>
      <div className="work-toolbar progress-metrics" aria-label="本月协作指标">
        <span>
          本月 AI 完成占比{" "}
          <strong>
            {metrics.isPending
              ? "…"
              : metrics.isError
                ? "暂不可用"
                : `${Math.round((metrics.data?.aiRatio ?? 0) * 100)}%`}
          </strong>
          （{metrics.data?.aiCompleted ?? 0}/{metrics.data?.totalCompleted ?? 0}
          ）
        </span>
        <span>
          和飞书未同步{" "}
          <strong>
            {metrics.data?.unsyncedCount ??
              (metrics.isError ? "暂不可用" : "…")}
          </strong>{" "}
          条
        </span>
      </div>
      {metrics.isError && (
        <p role="alert">指标读取失败：{metrics.error.message}</p>
      )}
      <nav className="work-tabs" aria-label="项目视图">
        <button
          aria-pressed={view === "table"}
          onClick={() => setView("table")}
        >
          进度表
        </button>
        <button
          aria-pressed={view === "board"}
          onClick={() => setView("board")}
        >
          项目看板
        </button>
      </nav>
      {view === "table" && !projects.length && !work.projects.isPending && (
        <p className="work-empty">暂无项目。新建项目或纳入飞书清单。</p>
      )}
      {view === "table" && (
        <div className="progress-table" aria-label="项目进度表">
          {projects.map((p) => {
            const tasks = (work.tasks.data ?? []).filter(
                (t) => t.projectId === p.id,
              ),
              isOpen = expandedProjects.includes(p.id);
            return (
              <section key={p.id}>
                <div className="progress-project">
                  <button
                    aria-expanded={isOpen}
                    onClick={() =>
                      setExpandedProjects((s) =>
                        isOpen ? s.filter((id) => id !== p.id) : [...s, p.id],
                      )
                    }
                  >
                    {isOpen ? "⌄" : "›"} <span>{p.title}</span>
                  </button>
                  <span>
                    {projectHealth[p.health]} <small>AI 标记</small>
                  </span>
                  <progress
                    aria-label={`${p.title}进度`}
                    max={100}
                    value={p.progress}
                  />
                  <span>{p.progress}%</span>
                  <button
                    onClick={() => {
                      setEditing(p);
                      setParams({ project: p.id });
                    }}
                  >
                    项目详情
                  </button>
                </div>
                <p className="work-meta">
                  健康依据：
                  {p.healthEvidence?.join("；") ||
                    (p.updates as ProjectUpdate[] | undefined)
                      ?.at(-1)
                      ?.evidence?.join("；") ||
                    (p.updates as ProjectUpdate[] | undefined)?.at(-1)
                      ?.content ||
                    "尚未附依据，请负责人核对"}
                </p>
                {isOpen && (
                  <div className="progress-tasks">
                    {tasks.map((t) => (
                      <article key={t.id} className="progress-task">
                        <div>
                          <Link to={`/tasks?task=${t.id}`}>{t.title}</Link>
                          <p className="work-meta">
                            {ownerLabel(t, directory.people.data ?? [])} ·
                            最后改动：
                            {sourceLabel(
                              t.lastChangeSource,
                              directory.people.data ?? [],
                            )}
                          </p>
                          <TaskFlags task={t} />
                        </div>
                        <select
                          aria-label={`${t.title}状态`}
                          value={t.status}
                          disabled={taskEdit.isPending}
                          onChange={(e) =>
                            taskEdit.mutate({
                              id: t.id,
                              patch: {
                                status: e.target.value as typeof t.status,
                                expectedUpdatedAt: t.updatedAt,
                              },
                            })
                          }
                        >
                          {Object.entries(statusLabels).map(([v, l]) => (
                            <option key={v} value={v}>
                              {l}
                            </option>
                          ))}
                        </select>
                        <input
                          aria-label={`${t.title}截止日`}
                          type="date"
                          value={
                            t.dueDate
                              ? taskDate(t.dueDate, "Asia/Shanghai")
                              : ""
                          }
                          disabled={taskEdit.isPending}
                          onChange={(e) =>
                            taskEdit.mutate({
                              id: t.id,
                              patch: {
                                dueDate: e.target.value || null,
                                expectedUpdatedAt: t.updatedAt,
                              },
                            })
                          }
                        />
                        <div className="progress-evidence">
                          <EvidenceLinks task={t} />
                        </div>
                      </article>
                    ))}
                    {!tasks.length && <p className="work-empty">暂无任务</p>}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
      {taskEdit.isError && <p role="alert">{taskEdit.error.message}</p>}
      {view === "board" && (
        <nav className="work-tabs" aria-label="项目分组">
          <button
            aria-pressed={group === "domain"}
            onClick={() => setGroup("domain")}
          >
            按业务域
          </button>
          <button
            aria-pressed={group === "health"}
            onClick={() => setGroup("health")}
          >
            按健康度
          </button>
        </nav>
      )}
      {adopt && (
        <form
          className="work-toolbar"
          onSubmit={(e) => {
            e.preventDefault();
            work.adopt.mutate(
              { guid, title: adoptTitle },
              {
                onError: (e) => setError(e.message),
                onSuccess: () => setAdopt(false),
              },
            );
          }}
        >
          <input
            required
            aria-label="飞书清单 ID"
            placeholder="飞书清单 ID"
            value={guid}
            onChange={(e) => setGuid(e.target.value)}
          />
          <input
            required
            aria-label="纳入项目名称"
            placeholder="项目名称"
            value={adoptTitle}
            onChange={(e) => setAdoptTitle(e.target.value)}
          />
          <button disabled={work.adopt.isPending}>纳入项目</button>
        </form>
      )}
      {(error || work.projects.error) && (
        <p role="alert">{error || work.projects.error?.message}</p>
      )}
      {view === "board" &&
        (work.projects.isPending ? (
          <p role="status">正在加载项目…</p>
        ) : projects.length === 0 && !work.projects.isError ? (
          <p className="work-empty">暂无项目。新建项目或纳入飞书清单。</p>
        ) : (
          groups
            .filter((g) =>
              projects.some(
                (p) => (group === "health" ? p.health : p.domain) === g,
              ),
            )
            .map((g) => (
              <section key={g} className="pj-group">
                <h2>
                  {group === "health"
                    ? projectHealth[g as keyof typeof projectHealth]
                    : g}
                </h2>
                <div className="work-grid">
                  {projects
                    .filter(
                      (p) => (group === "health" ? p.health : p.domain) === g,
                    )
                    .map((p) => {
                      const tasks = (work.tasks.data ?? []).filter(
                        (t) => t.projectId === p.id,
                      );
                      const progress = p.progress;
                      return (
                        <article key={p.id} className="work-project pj-card" data-health={p.health}>
                        <button
                          className="project-open"
                          onClick={() => {
                            setEditing(p);
                            setParams({ project: p.id });
                          }}
                        >
                          <span className="pj-top">
                            <span className="pj-health" data-health={p.health}>{projectHealth[p.health]}</span>
                            <span className="pj-status">{projectStatus[p.status]}</span>
                          </span>
                          <h3>{p.title}</h3>
                          <span className="pj-progress">
                            <progress max={100} value={progress} aria-label={`${p.title}进度`} />
                            <b>{progress}%</b>
                          </span>
                          <span className="pj-meta">
                            {(() => {
                              const owner = directory.people.data?.find((u) => u.id === p.ownerId)?.name;
                              return owner ? <span className="pj-owner"><i aria-hidden="true">{owner.slice(0, 1)}</i>{owner}</span> : <span>未指派</span>;
                            })()}
                            <span>{tasks.length} 件事</span>
                            <span>目标 {p.targetDate ?? "未定"}</span>
                          </span>
                          {p.milestones.length > 0 && (
                            <span className="pj-miles">
                              {p.milestones.map((m) => (
                                <span key={m.id} className="pj-mile" data-done={m.completed}>
                                  <time>{m.date.slice(5).replace("-", "/")}</time>
                                  {m.title}
                                </span>
                              ))}
                            </span>
                          )}
                        </button>
                      </article>
                      );
                    })}
                </div>
                {!projects.some(
                  (p) => (group === "health" ? p.health : p.domain) === g,
                ) && <p className="work-empty">暂无项目</p>}
              </section>
            ))
        ))}
      {(creating || active) && (
        <ProjectSheet
          key={active?.id ?? "new"}
          project={creating ? null : active}
          people={directory.people.data ?? []}
          customers={customers.data ?? []}
          results={(okr.data ?? []).flatMap((o) => o.keyResults)}
          tasks={(work.tasks.data ?? []).filter(
            (t) => t.projectId === active?.id,
          )}
          onClose={() => {
            setEditing(null);
            setCreating(false);
            setParams({});
          }}
          onSave={async (p) => {
            if (creating) await work.create.mutateAsync(p);
            else if (active)
              await work.update.mutateAsync({ id: active.id, patch: p });
            setCreating(false);
            setEditing(null);
            setParams({});
          }}
          work={work}
        />
      )}
    </section>
  );
}
function ProjectSheet({
  project,
  people,
  customers,
  results,
  tasks,
  onClose,
  onSave,
  work,
}: {
  project: Project | null;
  people: { id: string; name: string }[];
  customers: { id: string; name?: string; title?: string }[];
  results: { id: string; title: string }[];
  tasks: { id: string; title: string; status: keyof typeof statusLabels }[];
  onClose: () => void;
  onSave: (p: Partial<Project>) => Promise<void>;
  work: ReturnType<typeof useProjects>;
}) {
  const [draft, setDraft] = useState<Partial<Project>>(
      project ?? {
        title: "",
        ownerId: people[0]?.id ?? "",
        memberIds: [],
        status: "planned",
        health: "on_track",
        domain: BUSINESS_DOMAINS[0],
        startDate: null,
        targetDate: null,
        customerId: null,
        keyResultId: null,
      },
    ),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [mileTitle, setMileTitle] = useState(""),
    [mileDate, setMileDate] = useState(""),
    [kind, setKind] = useState<"milestone" | "delivery">("milestone"),
    [update, setUpdate] = useState(""),
    [updateId, setUpdateId] = useState<string | undefined>();
  const set = <K extends keyof Project>(k: K, v: Project[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));
  const perform = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!draft.title?.trim()) return setError("请填写项目名称");
    const {
      title,
      ownerId,
      memberIds,
      domain,
      status,
      health,
      startDate,
      targetDate,
      customerId,
      keyResultId,
      sourceUrl,
    } = draft;
    void perform(() =>
      onSave({
        title,
        ownerId,
        memberIds,
        domain,
        status,
        health,
        startDate,
        targetDate,
        customerId,
        keyResultId,
        sourceUrl,
      }),
    );
  };
  const formId = `project-sheet-${project?.id ?? "new"}`;
  useEffect(() => {
    const key = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);
  return (
    <div className="work-sheet-backdrop" onClick={onClose}>
      <aside
        className="work-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="项目详情"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sheet-head">
          <h2>{project ? "项目详情" : "新建项目"}</h2>
          <span className="sheet-head-spacer" />
          <button type="button" className="sheet-close" onClick={onClose} aria-label="关闭" title="关闭（Esc）">
            <X aria-hidden="true" />
          </button>
        </header>
        <div className="sheet-body">
          <form id={formId} className="work-form" onSubmit={submit}>
            <label>
              <span className="sheet-field-label">项目名称</span>
              <input
                autoFocus
                required
                className="sheet-title"
                placeholder="项目名称"
                value={draft.title}
                onChange={(e) => set("title", e.target.value)}
              />
            </label>
            <div className="sheet-props">
              <label className="sheet-prop">
                <span className="sheet-prop-k"><CircleDashed aria-hidden="true" />状态</span>
                <select value={draft.status} onChange={(e) => set("status", e.target.value as Project["status"])}>
                  {Object.entries(projectStatus).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                </select>
              </label>
              <label className="sheet-prop">
                <span className="sheet-prop-k"><Activity aria-hidden="true" />健康度</span>
                <select value={draft.health} onChange={(e) => set("health", e.target.value as Project["health"])}>
                  {Object.entries(projectHealth).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                </select>
              </label>
              <label className="sheet-prop">
                <span className="sheet-prop-k"><UserRound aria-hidden="true" />负责人</span>
                <select value={draft.ownerId} onChange={(e) => set("ownerId", e.target.value)}>
                  {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
              <div className="sheet-prop">
                <span className="sheet-prop-k"><UsersRound aria-hidden="true" />成员</span>
                <PeoplePicker label="成员" people={people} value={draft.memberIds ?? []} onChange={(ids) => set("memberIds", ids)} />
              </div>
              <label className="sheet-prop">
                <span className="sheet-prop-k"><CalendarDays aria-hidden="true" />开始日</span>
                <input type="date" value={draft.startDate ?? ""} onChange={(e) => set("startDate", e.target.value || null)} />
              </label>
              <label className="sheet-prop">
                <span className="sheet-prop-k"><Flag aria-hidden="true" />目标日</span>
                <input type="date" value={draft.targetDate ?? ""} onChange={(e) => set("targetDate", e.target.value || null)} />
              </label>
              <label className="sheet-prop">
                <span className="sheet-prop-k"><Shapes aria-hidden="true" />业务域</span>
                <select value={draft.domain} onChange={(e) => set("domain", e.target.value)}>
                  {BUSINESS_DOMAINS.map((d) => <option key={d}>{d}</option>)}
                </select>
              </label>
              <label className="sheet-prop">
                <span className="sheet-prop-k"><Building2 aria-hidden="true" />关联客户</span>
                <select value={draft.customerId ?? ""} onChange={(e) => set("customerId", e.target.value || null)}>
                  <option value="">不关联</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name ?? c.title ?? c.id}</option>)}
                </select>
              </label>
              <label className="sheet-prop">
                <span className="sheet-prop-k"><Target aria-hidden="true" />关联 OKR</span>
                <select value={draft.keyResultId ?? ""} onChange={(e) => set("keyResultId", e.target.value || null)}>
                  <option value="">不关联</option>
                  {results.map((k) => <option key={k.id} value={k.id}>{k.title}</option>)}
                </select>
              </label>
            </div>
          </form>
          {project && (
            <>
              <section className="sheet-section" aria-label="里程碑与交付节点">
                <h3>里程碑与交付节点</h3>
                {project.milestones.length > 0 && (
                  <ul className="pj-sheet-list">
                    {project.milestones.map((m) => (
                      <li key={m.id} data-done={m.completed}>
                        <input
                          type="checkbox"
                          aria-label={`完成 ${m.title}`}
                          checked={m.completed}
                          onChange={(e) => void perform(() => work.milestone.mutateAsync({ id: project.id, milestone: { ...m, completed: e.target.checked } }))}
                        />
                        <time>{m.date}</time>
                        <span className="pj-sheet-title">{m.title}</span>
                        <button
                          type="button"
                          className="pj-sheet-del"
                          aria-label={`删除 ${m.title}`}
                          title="删除"
                          disabled={busy}
                          onClick={() => void perform(() => work.removeMilestone.mutateAsync({ id: project.id, milestoneId: m.id }))}
                        >
                          <Trash2 aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="pj-sheet-add">
                  <input aria-label="里程碑名称" placeholder="新节点名称" value={mileTitle} onChange={(e) => setMileTitle(e.target.value)} />
                  <input type="date" aria-label="里程碑日期" value={mileDate} onChange={(e) => setMileDate(e.target.value)} />
                  <select aria-label="节点类型" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
                    <option value="milestone">项目里程碑</option>
                    <option value="delivery">客户交付</option>
                  </select>
                  <button
                    type="button"
                    disabled={!mileTitle || !mileDate || busy}
                    onClick={() =>
                      void perform(async () => {
                        await work.milestone.mutateAsync({ id: project.id, milestone: { title: mileTitle, date: mileDate, completed: false, kind } });
                        setMileTitle("");
                      })
                    }
                  >
                    添加节点
                  </button>
                </div>
              </section>
              <section className="sheet-section" aria-label="关联任务">
                <h3>关联任务 <span className="work-meta">{tasks.length}</span></h3>
                {tasks.length === 0 ? <p className="work-meta">还没有任务挂在这个项目下</p> : (
                  <ul className="pj-sheet-list">
                    {tasks.map((t) => (
                      <li key={t.id} data-done={t.status === "done"}>
                        <TaskCheck priority={0} status={t.status as TaskStatus} size="sm" />
                        <span className="pj-sheet-title">{t.title}</span>
                        <span className="work-meta">{statusLabels[t.status]}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              <ProjectContext projectId={project.id} />
              <section className="sheet-section" aria-label="每周进展">
                <h3>每周进展</h3>
                <ol className="sheet-timeline">
                  {((project.updates as ProjectUpdate[]) ?? []).map((u, i) => (
                    <li key={u.id ?? i} className="pj-update">
                      <p>{u.content}</p>
                      <span className="work-meta">
                        {u.health ? projectHealth[u.health] : ""}
                        {u.createdAt ? ` · ${new Date(u.createdAt).toLocaleDateString("zh-CN")}` : ""}
                      </span>
                      <button
                        type="button"
                        className="pj-update-edit"
                        onClick={() => {
                          setUpdate(u.content);
                          setUpdateId(u.id);
                          if (u.health) set("health", u.health);
                        }}
                      >
                        修改这条进展
                      </button>
                    </li>
                  ))}
                </ol>
                <div className="sheet-compose">
                  <textarea aria-label="项目进展" value={update} onChange={(e) => setUpdate(e.target.value)} placeholder="这周的进展、风险与下一步" />
                  <div className="sheet-compose-row">
                    <span />
                    <button
                      type="button"
                      disabled={!update.trim() || busy}
                      onClick={() =>
                        void perform(async () => {
                          await work.post.mutateAsync({ id: project.id, content: update, updateId, health: draft.health ?? project.health });
                          setUpdate("");
                          setUpdateId(undefined);
                        })
                      }
                    >
                      {updateId ? "保存进展修改" : "发布进展"}
                    </button>
                  </div>
                </div>
              </section>
            </>
          )}
        </div>
        <footer className="sheet-foot">
          {error && <p role="alert" className="work-error">{error}</p>}
          <button type="submit" form={formId} className="sheet-primary work-primary" disabled={busy}>
            保存项目
          </button>
        </footer>
      </aside>
    </div>
  );
}
